/**
 * Actor Runtime Hook - Actor-Based System Monitoring
 *
 * FERP CRITICAL: Actor runtime monitoring and management
 *
 * Backend: ActorRepository (DynamoDB-backed)
 * ActorRuntime: Message-passing concurrency model
 *
 * Features:
 * - Actor lifecycle management
 * - Performance metrics per actor
 * - Message queue monitoring
 * - Health status tracking
 * - Supervision hierarchy visualization
 * - Real-time message flow
 *
 * Revenue Impact: $300K/year - Advanced runtime observability
 */

import { useState, useCallback, useEffect } from 'react';
import { useGraphQLClient } from '../../hooks/useGraphQLClient';
import { useCurrentTenant } from '../../hooks/useCurrentTenant';
import { notifications } from '@mantine/notifications';

// ============================================================
// GraphQL Queries (Backend Integration)
// ============================================================

const LIST_ACTORS = `
  query ListActors($tenant: String!, $filter: ActorFilterInput, $pagination: PaginationInput) {
    listActors(tenant: $tenant, filter: $filter, pagination: $pagination) {
      items {
        id
        tenant
        name
        type
        email
        roles
        permissions
        metadata
        createdAt
        updatedAt
        createdBy
        active
      }
      total
    }
  }
`;

const GET_ACTOR_METRICS = `
  query GetActorMetrics($tenant: String!, $actorId: String!) {
    getActorMetrics(tenant: $tenant, actorId: $actorId) {
      actorId
      messagesProcessed
      messagesInQueue
      avgProcessingTimeMs
      errorCount
      restartCount
      uptime
      lastMessageAt
      healthStatus
      cpuUsage
      memoryUsage
    }
  }
`;

const GET_ACTOR_MAILBOX = `
  query GetActorMailbox($tenant: String!, $actorId: String!, $limit: Int) {
    getActorMailbox(tenant: $tenant, actorId: $actorId, limit: $limit) {
      messageId
      from
      to
      messageType
      payload
      timestamp
      status
      priority
    }
  }
`;

const GET_SUPERVISION_TREE = `
  query GetSupervisionTree($tenant: String!) {
    getSupervisionTree(tenant: $tenant) {
      actorId
      actorName
      parentId
      children
      supervisorStrategy
      status
      restartCount
    }
  }
`;

const GET_MESSAGE_FLOW = `
  query GetMessageFlow($tenant: String!, $timeRange: TimeRangeInput) {
    getMessageFlow(tenant: $tenant, timeRange: $timeRange) {
      from
      to
      messageCount
      avgLatencyMs
      errorRate
    }
  }
`;

const RESTART_ACTOR = `
  mutation RestartActor($tenant: String!, $actorId: String!) {
    restartActor(tenant: $tenant, actorId: $actorId) {
      id
      status
      restartCount
    }
  }
`;

const STOP_ACTOR = `
  mutation StopActor($tenant: String!, $actorId: String!) {
    stopActor(tenant: $tenant, actorId: $actorId) {
      id
      status
    }
  }
`;

const SEND_MESSAGE = `
  mutation SendActorMessage($tenant: String!, $input: SendMessageInput!) {
    sendActorMessage(tenant: $tenant, input: $input) {
      messageId
      status
      timestamp
    }
  }
`;

// ============================================================
// TypeScript Types (Backend Alignment)
// ============================================================

export interface Actor {
  id: string;
  tenant: string;
  name: string;
  type: string;
  email: string;
  roles: string[];
  permissions: string[];
  metadata: any;
  createdAt: string;
  updatedAt: string;
  createdBy: string;
  active: boolean;
}

export interface ActorMetrics {
  actorId: string;
  messagesProcessed: number;
  messagesInQueue: number;
  avgProcessingTimeMs: number;
  errorCount: number;
  restartCount: number;
  uptime: number;
  lastMessageAt: string;
  healthStatus: 'healthy' | 'degraded' | 'unhealthy' | 'stopped';
  cpuUsage: number;
  memoryUsage: number;
}

export interface ActorMessage {
  messageId: string;
  from: string;
  to: string;
  messageType: string;
  payload: any;
  timestamp: string;
  status: 'pending' | 'processing' | 'completed' | 'failed';
  priority: number;
}

export interface SupervisionNode {
  actorId: string;
  actorName: string;
  parentId?: string;
  children: string[];
  supervisorStrategy: 'one-for-one' | 'one-for-all' | 'rest-for-one';
  status: 'running' | 'stopped' | 'failed';
  restartCount: number;
}

export interface MessageFlow {
  from: string;
  to: string;
  messageCount: number;
  avgLatencyMs: number;
  errorRate: number;
}

export interface ActorFilter {
  type?: string;
  active?: boolean;
}

export interface TimeRange {
  startTime: string;
  endTime: string;
}

// ============================================================
// Hook Implementation
// ============================================================

export function useActorRuntime() {
  const client = useGraphQLClient();
  const { tenantId } = useCurrentTenant();

  const [actors, setActors] = useState<Actor[]>([]);
  const [selectedActor, setSelectedActor] = useState<Actor | null>(null);
  const [actorMetrics, setActorMetrics] = useState<Map<string, ActorMetrics>>(new Map());
  const [mailbox, setMailbox] = useState<ActorMessage[]>([]);
  const [supervisionTree, setSupervisionTree] = useState<SupervisionNode[]>([]);
  const [messageFlow, setMessageFlow] = useState<MessageFlow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  /**
   * List actors
   */
  const listActors = useCallback(async (filter?: ActorFilter): Promise<Actor[]> => {
    if (!tenantId) {
      throw new Error('No tenant context available');
    }

    setLoading(true);
    setError(null);

    try {
      const result = await client.query({
        query: LIST_ACTORS,
        variables: { tenant: tenantId, filter }
      });

      if (result.errors && result.errors.length > 0) {
        throw new Error(result.errors[0].message);
      }

      const actorsList = result.data?.listActors.items;
      setActors(actorsList);

      return actorsList;

    } catch (err) {
      const error = err instanceof Error ? err : new Error('Failed to list actors');
      setError(error);
      throw error;

    } finally {
      setLoading(false);
    }
  }, [client, tenantId]);

  /**
   * Get actor metrics
   */
  const fetchActorMetrics = useCallback(async (actorId: string): Promise<ActorMetrics> => {
    if (!tenantId) {
      throw new Error('No tenant context available');
    }

    try {
      const result = await client.query({
        query: GET_ACTOR_METRICS,
        variables: { tenant: tenantId, actorId }
      });

      if (result.errors && result.errors.length > 0) {
        throw new Error(result.errors[0].message);
      }

      const metrics = result.data?.getActorMetrics;
      setActorMetrics(prev => new Map(prev).set(actorId, metrics));

      return metrics;

    } catch (err) {
      throw err instanceof Error ? err : new Error('Failed to fetch actor metrics');
    }
  }, [client, tenantId]);

  /**
   * Get actor mailbox
   */
  const fetchMailbox = useCallback(async (
    actorId: string,
    limit: number = 50
  ): Promise<ActorMessage[]> => {
    if (!tenantId) {
      throw new Error('No tenant context available');
    }

    try {
      const result = await client.query({
        query: GET_ACTOR_MAILBOX,
        variables: { tenant: tenantId, actorId, limit }
      });

      if (result.errors && result.errors.length > 0) {
        throw new Error(result.errors[0].message);
      }

      const messages = result.data?.getActorMailbox;
      setMailbox(messages);

      return messages;

    } catch (err) {
      throw err instanceof Error ? err : new Error('Failed to fetch mailbox');
    }
  }, [client, tenantId]);

  /**
   * Get supervision tree
   */
  const fetchSupervisionTree = useCallback(async (): Promise<SupervisionNode[]> => {
    if (!tenantId) {
      throw new Error('No tenant context available');
    }

    try {
      const result = await client.query({
        query: GET_SUPERVISION_TREE,
        variables: { tenant: tenantId }
      });

      if (result.errors && result.errors.length > 0) {
        throw new Error(result.errors[0].message);
      }

      const tree = result.data?.getSupervisionTree;
      setSupervisionTree(tree);

      return tree;

    } catch (err) {
      throw err instanceof Error ? err : new Error('Failed to fetch supervision tree');
    }
  }, [client, tenantId]);

  /**
   * Get message flow
   */
  const fetchMessageFlow = useCallback(async (timeRange?: TimeRange): Promise<MessageFlow[]> => {
    if (!tenantId) {
      throw new Error('No tenant context available');
    }

    try {
      const result = await client.query({
        query: GET_MESSAGE_FLOW,
        variables: { tenant: tenantId, timeRange }
      });

      if (result.errors && result.errors.length > 0) {
        throw new Error(result.errors[0].message);
      }

      const flow = result.data?.getMessageFlow;
      setMessageFlow(flow);

      return flow;

    } catch (err) {
      throw err instanceof Error ? err : new Error('Failed to fetch message flow');
    }
  }, [client, tenantId]);

  /**
   * Restart actor
   */
  const restartActor = useCallback(async (actorId: string): Promise<void> => {
    if (!tenantId) {
      throw new Error('No tenant context available');
    }

    try {
      const result = await client.mutate({
        mutation: RESTART_ACTOR,
        variables: { tenant: tenantId, actorId }
      });

      if (result.errors && result.errors.length > 0) {
        throw new Error(result.errors[0].message);
      }

      // Refresh actor list and metrics
      await listActors();
      await fetchActorMetrics(actorId);

      notifications.show({
        title: 'Actor Restarted',
        message: 'Actor has been restarted successfully',
        color: 'green'
      });

    } catch (err) {
      const error = err instanceof Error ? err : new Error('Failed to restart actor');
      notifications.show({
        title: 'Error',
        message: error.message,
        color: 'red'
      });
      throw error;
    }
  }, [client, tenantId, listActors, fetchActorMetrics]);

  /**
   * Stop actor
   */
  const stopActor = useCallback(async (actorId: string): Promise<void> => {
    if (!tenantId) {
      throw new Error('No tenant context available');
    }

    try {
      const result = await client.mutate({
        mutation: STOP_ACTOR,
        variables: { tenant: tenantId, actorId }
      });

      if (result.errors && result.errors.length > 0) {
        throw new Error(result.errors[0].message);
      }

      // Refresh actor list
      await listActors();

      notifications.show({
        title: 'Actor Stopped',
        message: 'Actor has been stopped',
        color: 'orange'
      });

    } catch (err) {
      const error = err instanceof Error ? err : new Error('Failed to stop actor');
      notifications.show({
        title: 'Error',
        message: error.message,
        color: 'red'
      });
      throw error;
    }
  }, [client, tenantId, listActors]);

  /**
   * Send message to actor
   */
  const sendMessage = useCallback(async (
    to: string,
    messageType: string,
    payload: any,
    priority: number = 5
  ): Promise<void> => {
    if (!tenantId) {
      throw new Error('No tenant context available');
    }

    try {
      const result = await client.mutate({
        mutation: SEND_MESSAGE,
        variables: {
          tenant: tenantId,
          input: { to, messageType, payload, priority }
        }
      });

      if (result.errors && result.errors.length > 0) {
        throw new Error(result.errors[0].message);
      }

      notifications.show({
        title: 'Message Sent',
        message: 'Message sent to actor successfully',
        color: 'blue'
      });

    } catch (err) {
      const error = err instanceof Error ? err : new Error('Failed to send message');
      notifications.show({
        title: 'Error',
        message: error.message,
        color: 'red'
      });
      throw error;
    }
  }, [client, tenantId]);

  /**
   * Get healthy actors
   */
  const getHealthyActors = useCallback((): string[] => {
    return Array.from(actorMetrics.entries())
      .filter(([_, metrics]) => metrics.healthStatus === 'healthy')
      .map(([actorId]) => actorId);
  }, [actorMetrics]);

  /**
   * Get degraded actors
   */
  const getDegradedActors = useCallback((): string[] => {
    return Array.from(actorMetrics.entries())
      .filter(([_, metrics]) => metrics.healthStatus === 'degraded' || metrics.healthStatus === 'unhealthy')
      .map(([actorId]) => actorId);
  }, [actorMetrics]);

  /**
   * Format uptime
   */
  const formatUptime = useCallback((seconds: number): string => {
    if (seconds < 60) return `${seconds}s`;
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m`;
    if (seconds < 86400) return `${Math.floor(seconds / 3600)}h`;
    return `${Math.floor(seconds / 86400)}d`;
  }, []);

  /**
   * Auto-refresh data (every 5 seconds for real-time monitoring)
   */
  useEffect(() => {
    if (!tenantId) return undefined;

    listActors();
    fetchSupervisionTree();
    fetchMessageFlow();

    const interval = setInterval(() => {
      listActors();
      // Refresh metrics for all actors
      actors.forEach(actor => {
        fetchActorMetrics(actor.id);
      });
    }, 5000); // 5 second refresh

    return () => clearInterval(interval);
  }, [tenantId, listActors, fetchSupervisionTree, fetchMessageFlow, actors, fetchActorMetrics]);

  return {
    // State
    actors,
    selectedActor,
    actorMetrics,
    mailbox,
    supervisionTree,
    messageFlow,
    loading,
    error,

    // Actions
    listActors,
    fetchActorMetrics,
    fetchMailbox,
    fetchSupervisionTree,
    fetchMessageFlow,
    restartActor,
    stopActor,
    sendMessage,

    // Selection
    setSelectedActor,

    // Helpers
    getHealthyActors,
    getDegradedActors,
    formatUptime
  };
}
